import { updateState, withDevtools } from "@angular-architects/ngrx-toolkit";
import { inject } from "@angular/core";
import { UserClientEntityDto } from "@dtos/users-clients-entities.dto";
import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { ClientUsersProvider } from "@providers2/client-users.provider";
import { Helpers } from "@utils/helpers";

export class UserClientState {
  userClient: UserClientEntityDto = new UserClientEntityDto();
  errors: string[] = [];
}

export const UserClientStore = signalStore(
  {
    providedIn: 'root',
    // Configuración para Redux DevTools
    ...(typeof window !== 'undefined' && (window as any).__REDUX_DEVTOOLS_EXTENSION__ && {
      devtools: {
        name: 'RoadIt UserClient Store',
        trace: true,
        traceLimit: 25
      }
    })
  },
  withDevtools('UserClientStore'),
  // Estado inicial
  withState(new UserClientState()),
  // Métodos globales
  withMethods((store) => {
    return {
      updateState: (updates: Partial<UserClientState>) => {
        patchState(store, { ...updates });
        saveUserClientStoreToStorage(store);
      },
      initializeFromStorage: () => {
        const loadedStore = loadUserClientStoreFromStorage();
        if (loadedStore) {
          patchState(store, loadedStore);
        }
      },
      destroy: () => {
        patchState(store, new UserClientState());
      }
    }
  }),
  // Métodos que acceden a providers
  withMethods((store) => {
    const prov = inject(ClientUsersProvider);
    return {
      clearStore: () => {
        store.updateState(new UserClientState());
        clearUserClientStoreFromStorage();
      },

      loadById: async (userId: number) => {
        try {
          const userData = await prov.getClientUserById(userId);
          if (!userData) {
            console.error('No se pudo cargar el usuario cliente-entidad');
            return;
          }
          store.updateState({
            userClient: userData,
            errors: []
          });
        } catch (error) {
          console.error('Error al cargar usuario cliente-entidad por ID:', error);
          return;
        }
      },

      create: async (userData: Partial<UserClientEntityDto>, clientIds?: number[], password?: string): Promise<UserClientEntityDto | null> => {
        try {
          const createdUser = await prov.createClientUser(userData, clientIds, password);
          if (!createdUser) {
            console.error('No se pudo crear el usuario cliente-entidad');
            return null;
          }
          // ACTUALIZAR EL ESTADO CON EL USUARIO CREADO
          store.updateState({
            userClient: createdUser,
            errors: []
          });
          return createdUser;
        } catch (error) {
          console.error('Error al crear usuario cliente-entidad:', error);
          store.updateState({
            errors: ['Error al crear usuario cliente-entidad']
          });
          return null;
        }
      },

      update: async (userId: number, userData: Partial<UserClientEntityDto>, clientIds?: number[], password?: string): Promise<UserClientEntityDto | null> => {
        try {
          const updatedUser = await prov.updateClientUser(userId, userData, clientIds, password);
          if (!updatedUser) {
            console.error('No se pudo actualizar el usuario cliente-entidad');
            return null;
          }
          // ACTUALIZAR EL ESTADO CON EL USUARIO ACTUALIZADO
          store.updateState({
            userClient: updatedUser,
            errors: []
          });
          return updatedUser;
        } catch (error) {
          console.error('Error al actualizar usuario cliente-entidad:', error);
          store.updateState({
            errors: ['Error al actualizar usuario cliente-entidad']
          });
          return null;
        }
      },

      delete: async (userId: number): Promise<boolean> => {
        try {
          const success = await prov.deleteClientUser(userId);
          if (!success) {
            console.error('No se pudo eliminar el usuario cliente-entidad');
            return false;
          }
          return true;
        } catch (error) {
          console.error('Error al eliminar usuario cliente-entidad:', error);
          return false;
        }
      }, 
      clear: () => {
        store.updateState({
          userClient: new UserClientEntityDto(),
          errors: []
        });
      }
    }
  })
);

function saveUserClientStoreToStorage(store: any, key = 'roadit_user_client_store') {
  const result: Record<string, unknown> = {};
  for (const prop of Object.keys(new UserClientState())) {
    result[prop] = store[prop]();
  }
  Helpers.setSessionStorage(key, result);
}

function loadUserClientStoreFromStorage(key = 'roadit_user_client_store'): UserClientState | null {
  return Helpers.getSessionStorage<UserClientState>(key);
}

function clearUserClientStoreFromStorage(key = 'roadit_user_client_store') {
  Helpers.removeSessionStorage(key);
}
